// Shared styling + rendering helpers for live traffic-incident markers.
// Backend (TomTom v5) returns each incident already classified into a `kind`.

export const INCIDENT_STYLES = {
  accident: { emoji: "🔴", label: "Accident" },
  closure: { emoji: "⛔", label: "Road closure" },
  roadworks: { emoji: "🚧", label: "Roadworks" },
  congestion: { emoji: "🟠", label: "Congestion" },
  congestion_severe: { emoji: "🟠", label: "Severe congestion" },
  disruption: { emoji: "⚠️", label: "Disruption" },
  event: { emoji: "🎫", label: "Local event" },
  weather: { emoji: "🌦", label: "Weather" },
};

export const styleFor = (kind) => INCIDENT_STYLES[kind] || INCIDENT_STYLES.disruption;

// Severity rank derived from `kind` (no numeric severity comes from the backend).
// Higher = more severe. Used for display prioritisation ONLY — scoring/risk logic
// on the backend is untouched.
const SEVERITY_RANK = {
  closure: 4,
  accident: 4,
  congestion_severe: 3,
  weather: 2,
  roadworks: 2,
  event: 2,
  congestion: 1,
  disruption: 1,
};
export const severityRank = (kind) => SEVERITY_RANK[kind] ?? 1;
const isSevere = (inc) => severityRank(inc.kind) >= 4; // closures / accidents

// Live counters over the FULL available incident set (not just rendered markers).
export function incidentCounts(incidents = []) {
  let affecting = 0;
  let nearby = 0;
  let events = 0;
  (incidents || []).forEach((inc) => {
    if (inc.kind === "event") {
      if (inc.affects) events += 1;
      else nearby += 1;
    } else if (inc.affects) {
      affecting += 1;
    } else {
      nearby += 1;
    }
  });
  return { affecting, nearby, events, total: (incidents || []).length };
}

// Display prioritisation (default route-only view). Scoring is unchanged; this only
// decides which markers are drawn so the map does not become cluttered.
// Order: affects route → severity → estimated delay → distance → timing relevance.
// Guarantees affecting incidents and severe route-relevant closures are always shown.
export function prioritiseIncidents(incidents = [], { cap = 12, showAll = false } = {}) {
  const all = incidents || [];
  const counts = incidentCounts(all);
  if (showAll) return { display: all, hidden: 0, counts };

  const startMs = (inc) => {
    const t = inc.start_time ? Date.parse(inc.start_time) : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  const sorted = [...all].sort((a, b) => {
    if (!!b.affects !== !!a.affects) return b.affects ? 1 : -1;
    const sev = severityRank(b.kind) - severityRank(a.kind);
    if (sev) return sev;
    const delay = (b.delay_mins || 0) - (a.delay_mins || 0);
    if (delay) return delay;
    const dist = (a.distance_m || 0) - (b.distance_m || 0);
    if (dist) return dist;
    return startMs(a) - startMs(b); // sooner start = more relevant
  });

  // Always-keep set: affecting incidents + severe route-relevant closures/accidents.
  const mustKeep = sorted.filter((i) => i.affects || (isSevere(i) && i.route_relevant));
  const rest = sorted.filter((i) => !mustKeep.includes(i));
  const display = [...mustKeep];
  for (const inc of rest) {
    if (display.length >= cap) break;
    display.push(inc);
  }
  return { display, hidden: Math.max(0, all.length - display.length), counts };
}

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
};

const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// HTML string for a Leaflet popup / Google InfoWindow info card.
export function incidentPopupHtml(inc) {
  const s = styleFor(inc.kind);
  const start = fmtTime(inc.start_time);
  const end = fmtTime(inc.end_time);
  const dist = inc.distance_m >= 1000
    ? `${(inc.distance_m / 1609.34).toFixed(1)} mi`
    : `~${inc.distance_m} m`;
  const rows = [];
  if (inc.location) rows.push(`<div style="font-weight:600;color:#334155">${esc(inc.location)}</div>`);
  if (inc.description) rows.push(`<div style="color:#475569;margin-top:2px">${esc(inc.description)}</div>`);
  if (inc.relevance) rows.push(`<div style="color:#6d28d9;margin-top:3px;font-style:italic">Why relevant: ${esc(inc.relevance)}</div>`);
  const meta = [];
  if (start) meta.push(`<div>Start: ${esc(start)}</div>`);
  if (end) meta.push(`<div>End: ${esc(end)}</div>`);
  if (inc.delay_mins != null) meta.push(`<div>Estimated delay: <b>+${inc.delay_mins} min</b></div>`);
  meta.push(`<div>${dist} from planned route</div>`);
  meta.push(`<div style="font-weight:700;margin-top:2px;color:${
    inc.affects ? "#b45309" : (inc.route_relevant === false ? "#64748b" : "#16a34a")}">${
    inc.affects ? "Affects this Shift Run" : (inc.route_relevant === false ? "Elsewhere in the area (not on route)" : "Near the route (minor)")}</div>`);
  let links = "";
  if (inc.kind === "event" && (inc.url || inc.venue_url)) {
    const a = [];
    if (inc.url) a.push(`<a href="${esc(inc.url)}" target="_blank" rel="noopener noreferrer" style="color:#4F46E5;font-weight:600;text-decoration:none">View event ↗</a>`);
    if (inc.venue_url) a.push(`<a href="${esc(inc.venue_url)}" target="_blank" rel="noopener noreferrer" style="color:#4F46E5;font-weight:600;text-decoration:none">View venue ↗</a>`);
    links = `<div style="margin-top:6px;display:flex;gap:12px">${a.join("")}</div>`;
  }
  return `<div style="font:12px/1.45 system-ui,-apple-system,sans-serif;min-width:190px" data-testid="incident-card">
    <div style="font-weight:800;font-size:13px;color:#0f172a">${s.emoji} ${esc(inc.type_label)}</div>
    ${rows.join("")}
    <div style="margin-top:5px;color:#64748b">${meta.join("")}</div>
    ${links}
  </div>`;
}

// Leaflet emoji divIcon for a given incident.
export function incidentDivIcon(L, inc) {
  const s = styleFor(inc.kind);
  const op = inc.affects ? 1 : (inc.route_relevant === false ? 0.35 : 0.55);
  return L.divIcon({
    className: "",
    html: `<div style="font-size:20px;line-height:20px;opacity:${op};filter:drop-shadow(0 1px 2px rgba(0,0,0,.45))">${s.emoji}</div>`,
    iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -10],
  });
}
